import React, { Dispatch, FC, useEffect } from 'react'
import { ApiRetailerPaymentType } from '@vend/types'
import { getMainTender } from '../../../../../../functions/payments'
import {
  SwiperPaymentEvent,
  SwiperPaymentState,
} from '../../hooks/use-swiper-payment-reducer/types'
import { ApproveSignature } from '../Signature/ApproveSignature'
import { PrintSignatureReceipt } from '../Signature/PrintSignatureReceipt'
import { Error } from '../Error/Error'
import { PartialApprove } from '../PartialApprove/PartialApprove'
import { ProcessingTransaction } from '../ProcessingTransaction/ProcessingTransaction'
import { Reversing } from '../Reversing/Reversing'
import { handleTransaction } from '../ProcessingTransaction/handleTransaction'
import { usePaymentManager } from '../../../../../../ng-compat/hooks'
import { useErrorMessages } from '../../hooks/useErrorMessages'

interface Props {
  state: SwiperPaymentState
  registerID: string
  registerSalePaymentID: string
  retailerPaymentType: ApiRetailerPaymentType
  amount: number
  currency: string
  originalRegisterSalePaymentID?: string
  dispatch: Dispatch<SwiperPaymentEvent>
  isRefund: boolean
}

export const SwiperPaymentModalContent: FC<Props> = ({
  registerID,
  registerSalePaymentID,
  amount,
  currency,
  state,
  dispatch,
  retailerPaymentType,
  originalRegisterSalePaymentID,
  isRefund,
}) => {
  const paymentManager = usePaymentManager()
  const {
    getTransactionErrorMessages,
    getSwiperErrorMessage,
  } = useErrorMessages()
  useEffect(() => {
    if (state.status === 'error' && state.shouldPrintReceipt) {
      paymentManager.emit('printReceipt')
    }
  }, [paymentManager, state])

  switch (state.status) {
    case 'processingTransaction': {
      return (
        <ProcessingTransaction
          isRefund={isRefund}
          registerID={registerID}
          retailerPaymentTypeID={retailerPaymentType.id}
          registerSalePaymentID={registerSalePaymentID}
          amount={amount}
          currency={currency}
          onComplete={transaction =>
            handleTransaction(
              transaction,
              dispatch,
              registerID,
              getTransactionErrorMessages,
              getSwiperErrorMessage
            )
          }
          onError={errorMessage => dispatch({ type: 'ERROR', errorMessage })}
          originalRegisterSalePaymentID={originalRegisterSalePaymentID}
          paymentTypeID={retailerPaymentType.type_id}
        />
      )
    }
    case 'approvingPartialPayment': {
      return (
        <PartialApprove
          approvedAmount={getMainTender(state.transaction.tenders)?.amount}
          onAccept={() =>
            dispatch({ type: 'DONE', transaction: state.transaction })
          }
          onReverse={() =>
            dispatch({
              type: 'REVERSE_TRANSACTION',
              registerID,
              registerSalePaymentID,
            })
          }
        />
      )
    }
    case 'reversing': {
      return (
        <Reversing
          registerID={state.registerID}
          registerSalePaymentID={state.registerSalePaymentID}
          retailerPaymentTypeID={retailerPaymentType.id}
          onComplete={transaction =>
            handleTransaction(
              transaction,
              dispatch,
              registerID,
              getTransactionErrorMessages,
              getSwiperErrorMessage
            )
          }
          onError={errorMessage => dispatch({ type: 'ERROR', errorMessage })}
        />
      )
    }
    case 'reversed':
    case 'error': {
      return (
        <Error
          errorMessage={state.errorMessage}
          onClose={() => dispatch({ type: 'DONE' })}
        />
      )
    }
    case 'printingSignatureReceipt': {
      return (
        <PrintSignatureReceipt
          onDecline={() =>
            dispatch({
              type: 'REVERSE_TRANSACTION',
              registerID,
              registerSalePaymentID,
            })
          }
          onComplete={() => {
            dispatch({
              type: 'PROMPT_APPROVE_SIGNATURE',
              transaction: state.transaction,
            })
          }}
          retailerPaymentType={retailerPaymentType}
          transaction={state.transaction}
        />
      )
    }
    case 'approvingSignature': {
      return (
        <ApproveSignature
          onAccept={() =>
            dispatch({ type: 'DONE', transaction: state.transaction })
          }
          onDecline={() =>
            dispatch({
              type: 'REVERSE_TRANSACTION',
              registerID,
              registerSalePaymentID,
            })
          }
          signatureSrc={state.signatureSrc}
        />
      )
    }
    case 'done': {
      return null
    }
    default: {
      // This `never` typing will let us know at compile time if we do not handle an existing status. Neat !
      const _exhaustiveCheck: never = state
      return _exhaustiveCheck
    }
  }
}
